import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Autocomplete,
  Alert,
  Switch,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../../../contexts/AuthContext';
import { smartWarehouseService, CreateSmartWarehouseDto, UpdateSmartWarehouseDto } from '../services/smartWarehouseService';
import { orderTransferService } from '../services/orderTransferService';
import {
  DEFAULT_TRANSFER_CHANNELS,
  MARKETPLACE_TRANSFER_CHANNELS,
  WEBSITE_CHANNEL_SOURCE,
  resolveChannelLabel,
} from '../screens/picking/pickingChannels';
import { useTranslation } from 'react-i18next';

interface SmartWarehouseData {
  Id: string;
  Name: string;
  Address?: string;
  Phone?: string;
  ResponsiblePerson?: string;
  Description?: string;
  IsDefault: boolean;
  BranchesId?: string;
  LocationModel?: string;
  TotalCapacity?: number;
  DefaultRoutingStrategy?: string;
}

interface SmartWarehouseModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editData?: SmartWarehouseData | null;
}

const LOCATION_MODEL_OPTIONS = [
  { value: 'ZonePlatformRack', labelKey: 'components.smartWarehouseModal.locationModel3Level' },
  { value: 'ZonePlatformRackLevelBin', labelKey: 'components.smartWarehouseModal.locationModel5Level' },
];

const ROUTING_STRATEGY_OPTIONS = [
  { value: 'SShapeSkip', label: 'S-Shape + Koridor Atlama' },
];

const AUTO_TRANSFER_MARKER = '##AUTO_TRANSFER##';
const AUTO_TRANSFER_END = '##END_AT##';

/** Açıklama alanından otomatik aktarım JSON bloğunu temizler. */
const stripAutoTransferFromDescription = (raw?: string | null): string => {
  if (!raw) return '';
  let desc = raw;
  const markerIdx = desc.indexOf(AUTO_TRANSFER_MARKER);
  if (markerIdx >= 0) {
    const endIdx = desc.indexOf(AUTO_TRANSFER_END, markerIdx);
    if (endIdx >= 0) {
      desc = desc.slice(0, markerIdx) + desc.slice(endIdx + AUTO_TRANSFER_END.length);
    } else {
      desc = desc.slice(0, markerIdx);
    }
  } else {
    // Bozulmuş kayıt: başta JSON + ##END_AT##
    const endOnly = desc.indexOf(AUTO_TRANSFER_END);
    if (endOnly >= 0 && desc.trimStart().startsWith('{')) {
      desc = desc.slice(endOnly + AUTO_TRANSFER_END.length);
    }
  }
  return desc.trim();
};

interface BranchOption {
  id: string;
  name: string;
}

const SmartWarehouseModal: React.FC<SmartWarehouseModalProps> = ({
  open,
  onClose,
  onSaved,
  editData,
}) => {
  const { t } = useTranslation('warehouse');
  const { branches, companyName } = useAuth();

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [description, setDescription] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<BranchOption | null>(null);
  const [locationModel, setLocationModel] = useState('ZonePlatformRackLevelBin');
  const [routingStrategy, setRoutingStrategy] = useState('SShapeSkip');
  const [totalCapacity, setTotalCapacity] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Auto-transfer settings
  const [autoTransferEnabled, setAutoTransferEnabled] = useState(false);
  const [autoTransferTrigger, setAutoTransferTrigger] = useState('Confirmed');
  const [autoTransferPriority, setAutoTransferPriority] = useState(0);
  const [enabledChannels, setEnabledChannels] = useState<string[]>([...DEFAULT_TRANSFER_CHANNELS]);

  useEffect(() => {
    if (open) {
      if (editData) {
        setName(editData.Name || '');
        setAddress(editData.Address || '');
        setPhone(editData.Phone || '');
        setResponsiblePerson(editData.ResponsiblePerson || '');
        // AUTO_TRANSFER bloğu açıklama alanına sızmasın (ayrı endpoint'ten yönetilir).
        setDescription(stripAutoTransferFromDescription(editData.Description || ''));
        setLocationModel(editData.LocationModel || 'ZonePlatformRackLevelBin');
        // Eski kayıtlar 'SShape' olabilir; backend bunları SShapeSkip'e map eder.
        setRoutingStrategy(editData.DefaultRoutingStrategy || 'SShapeSkip');
        setTotalCapacity(editData.TotalCapacity ? String(editData.TotalCapacity) : '');
        setIsDefault(editData.IsDefault || false);
        if (editData.BranchesId) {
          const branch = branches.find((b) => b.id === editData.BranchesId);
          setSelectedBranch(branch ? { id: branch.id, name: branch.name } : null);
        } else {
          setSelectedBranch(null);
        }
        // Load auto-transfer settings
        orderTransferService.getAutoTransferSettings(editData.Id)
          .then(s => {
            setAutoTransferEnabled(s.enabled);
            setAutoTransferTrigger(s.triggerStatus || 'Confirmed');
            setAutoTransferPriority(s.defaultPriority || 0);
            // null = eski kayıt → tüm kanallar. Dizi (boş dahil) = kaydedilmiş seçim.
            setEnabledChannels(
              s.enabledChannels == null
                ? [...DEFAULT_TRANSFER_CHANNELS]
                : s.enabledChannels.map((c) => c.toLowerCase()),
            );
          })
          .catch(() => { /* ignore */ });
      } else {
        resetForm();
      }
    }
  }, [open, editData, branches]);

  const resetForm = () => {
    setName('');
    setAddress('');
    setPhone('');
    setResponsiblePerson('');
    setDescription('');
    setSelectedBranch(null);
    setLocationModel('ZonePlatformRackLevelBin');
    setRoutingStrategy('SShapeSkip');
    setTotalCapacity('');
    setIsDefault(false);
    setError('');
    setAutoTransferEnabled(false);
    setAutoTransferTrigger('Confirmed');
    setAutoTransferPriority(0);
    setEnabledChannels([...DEFAULT_TRANSFER_CHANNELS]);
  };

  const toggleChannel = (source: string) => {
    setEnabledChannels((prev) =>
      prev.includes(source) ? prev.filter((c) => c !== source) : [...prev, source],
    );
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('components.smartWarehouseModal.errorNameRequired'));
      return;
    }
    if (!locationModel) {
      setError(t('components.smartWarehouseModal.errorLocationModelRequired'));
      return;
    }
    if (autoTransferEnabled && enabledChannels.length === 0) {
      setError(t('components.smartWarehouseModal.errorChannelsRequired'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const cleanDescription = stripAutoTransferFromDescription(description.trim()) || undefined;

      if (editData) {
        const dto: UpdateSmartWarehouseDto = {
          Id: editData.Id,
          Name: name.trim(),
          Address: address.trim() || undefined,
          Phone: phone.trim() || undefined,
          ResponsiblePerson: responsiblePerson.trim() || undefined,
          Description: cleanDescription,
          IsDefault: isDefault,
          BranchesId: selectedBranch?.id || undefined,
          LocationModel: locationModel,
          TotalCapacity: totalCapacity ? parseInt(totalCapacity, 10) : undefined,
          DefaultRoutingStrategy: routingStrategy,
        };
        await smartWarehouseService.update(dto);

        // Kanal ayarı depo update'ten sonra yazılır; hata olursa kullanıcıya göster.
        await orderTransferService.updateAutoTransferSettings({
          warehouseId: editData.Id,
          enabled: autoTransferEnabled,
          triggerStatus: autoTransferTrigger,
          defaultPriority: autoTransferPriority,
          // Auto-transfer kapalı olsa bile kanal seçimini sakla (toplama ekranı buradan filtreler).
          enabledChannels,
        });
      } else {
        const dto: CreateSmartWarehouseDto = {
          Name: name.trim(),
          Address: address.trim() || undefined,
          Phone: phone.trim() || undefined,
          ResponsiblePerson: responsiblePerson.trim() || undefined,
          Description: cleanDescription,
          IsDefault: isDefault,
          BranchesId: selectedBranch?.id || undefined,
          LocationModel: locationModel,
          TotalCapacity: totalCapacity ? parseInt(totalCapacity, 10) : undefined,
          DefaultRoutingStrategy: routingStrategy,
        };
        await smartWarehouseService.create(dto);
      }

      onSaved();
      handleClose();
    } catch (err: any) {
      const msg = err?.response?.data?.Message || err?.response?.data?.message || t('components.smartWarehouseModal.errorSaveFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {editData ? t('components.smartWarehouseModal.editTitle') : t('components.smartWarehouseModal.addTitle')}
          </Typography>
          <IconButton edge="end" onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          label={t('components.smartWarehouseModal.nameLabel')}
          fullWidth
          value={name}
          onChange={(e) => { setName(e.target.value); setError(''); }}
          disabled={loading}
          required
          sx={{ mb: 2, mt: 1 }}
        />

        <TextField
          label={t('components.smartWarehouseModal.addressLabel')}
          fullWidth
          multiline
          rows={2}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        <TextField
          label={t('components.smartWarehouseModal.phoneLabel')}
          fullWidth
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        <TextField
          label={t('components.smartWarehouseModal.responsibleLabel')}
          fullWidth
          value={responsiblePerson}
          onChange={(e) => setResponsiblePerson(e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        <TextField
          label={t('components.smartWarehouseModal.descriptionLabel')}
          fullWidth
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        <Autocomplete
          value={selectedBranch}
          onChange={(_e, newValue) => setSelectedBranch(newValue)}
          options={branches.map((b) => ({ id: b.id, name: b.name }))}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                <Typography variant="body2">{option.name}</Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label={t('components.smartWarehouseModal.branchLabel')} placeholder={t('components.smartWarehouseModal.branchPlaceholder')} />
          )}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('components.smartWarehouseModal.locationModelLabel')}</InputLabel>
          <Select
            value={locationModel}
            label={t('components.smartWarehouseModal.locationModelLabel')}
            onChange={(e) => setLocationModel(e.target.value)}
            disabled={loading}
          >
            {LOCATION_MODEL_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>{t('components.smartWarehouseModal.routingStrategyLabel')}</InputLabel>
          <Select
            value={routingStrategy}
            label={t('components.smartWarehouseModal.routingStrategyLabel')}
            onChange={(e) => setRoutingStrategy(e.target.value)}
            disabled={loading}
          >
            {ROUTING_STRATEGY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={t('components.smartWarehouseModal.totalCapacityLabel')}
          fullWidth
          type="number"
          value={totalCapacity}
          onChange={(e) => setTotalCapacity(e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
          inputProps={{ min: 0 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              disabled={loading}
            />
          }
          label={t('components.smartWarehouseModal.setDefaultWarehouse')}
        />

        {editData && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t('components.smartWarehouseModal.enabledChannelsLabel')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('components.smartWarehouseModal.enabledChannelsHint')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {MARKETPLACE_TRANSFER_CHANNELS.map((ch) => {
                const label = ch.source === WEBSITE_CHANNEL_SOURCE
                  ? resolveChannelLabel(ch, companyName) || ch.label
                  : ch.label;
                return (
                  <FormControlLabel
                    key={ch.source}
                    control={
                      <Checkbox
                        size="small"
                        checked={enabledChannels.includes(ch.source)}
                        onChange={() => toggleChannel(ch.source)}
                        disabled={loading}
                      />
                    }
                    label={<Typography variant="body2">{label}</Typography>}
                  />
                );
              })}
            </Box>

            <Typography variant="subtitle2" gutterBottom>{t('components.smartWarehouseModal.autoTransferTitle')}</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={autoTransferEnabled}
                  onChange={(e) => setAutoTransferEnabled(e.target.checked)}
                  disabled={loading}
                />
              }
              label={t('components.smartWarehouseModal.autoTransferEnabled')}
            />
            {autoTransferEnabled && (
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>{t('components.smartWarehouseModal.triggerStatusLabel')}</InputLabel>
                  <Select value={autoTransferTrigger} label={t('components.smartWarehouseModal.triggerStatusLabel')}
                    onChange={(e) => setAutoTransferTrigger(e.target.value)} disabled={loading}>
                    <MenuItem value="Confirmed">{t('components.smartWarehouseModal.triggerConfirmed')}</MenuItem>
                    <MenuItem value="Paid">{t('components.smartWarehouseModal.triggerPaid')}</MenuItem>
                    <MenuItem value="Received">{t('components.smartWarehouseModal.triggerReceived')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>{t('components.smartWarehouseModal.priorityLabel')}</InputLabel>
                  <Select value={autoTransferPriority} label={t('components.smartWarehouseModal.priorityLabel')}
                    onChange={(e) => setAutoTransferPriority(Number(e.target.value))} disabled={loading}>
                    <MenuItem value={0}>{t('components.smartWarehouseModal.priorityNormal')}</MenuItem>
                    <MenuItem value={1}>{t('components.smartWarehouseModal.priorityHigh')}</MenuItem>
                    <MenuItem value={2}>{t('components.smartWarehouseModal.priorityUrgent')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !name.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? t('components.smartWarehouseModal.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SmartWarehouseModal;
